import { forwardRef, useImperativeHandle, type ReactNode } from 'react';
// Import Types
import type { FilterTagRef } from 'types';
// Import Components
import FilterTagModals from './FilterTagModals';
// Import Hooks
import { useFilterTags, type UseFilterTagsParams } from './useFilterTags';
// Import Context
import { FilterTagsContext } from './FilterTagsContext';

export interface FilterTagsProviderProps extends UseFilterTagsParams {
    children: ReactNode;
}

/**
 * Headless replacement for the `FilterTag` card. It keeps the `FilterTagRef` contract used by `FilterHolder`
 * (`changeFiltersList`) and exposes chips + save / saved-filters actions through `FilterTagsContext`,
 * so a `DataTable` rendered inside can show them in its compact header.
 */
export const FilterTagsProvider = forwardRef<FilterTagRef, FilterTagsProviderProps>(({ children, ...params }, ref) => {
    const filterTags = useFilterTags(params);
    const { changeFiltersList, contextValue } = filterTags;

    useImperativeHandle(ref, () => ({ changeFiltersList }), [changeFiltersList]);

    return (
        <FilterTagsContext.Provider value={contextValue}>
            {children}
            <FilterTagModals filtersTagKey={params.filtersTagKey} filterTags={filterTags} />
        </FilterTagsContext.Provider>
    );
});

FilterTagsProvider.displayName = 'FilterTagsProvider';
